import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  TextField,
  Grid,
  Typography,
  Stack,
  Box,
  Paper,
  InputAdornment,
  IconButton,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import validationRules from '../../utils/validations.util';
import SnackbarInfo from '../common/snackbar-info.component';
import {
  createNewCredential,
  loginAsAdminUser,
} from '../../services/distributor.service';
import {
  updateDataById,
  deleteDataById,
} from '../../services/admin-users.service';
import Iconify from '../common/iconify.component';
import { getDeviceModel, getDeviceOs } from '../../utils/device-info.util';
import { MODULE_ACCESS } from '../../config/module-access.config';
import ConfirmPopup from '../common/confirm-popup.component';

const ContentOwnerLogin = ({
  distributorDetail,
  handleRefreshData,
  formValueChangedCallback,
}) => {
  const {
    control,
    formState: { errors, dirtyFields },
    handleSubmit,
    unregister,
  } = useForm({
    defaultValues: {
      name:
        distributorDetail.adminFullName ||
        `${distributorDetail.firstName} ${distributorDetail.lastName}` ||
        '',
      email: distributorDetail.adminEmail || distributorDetail.email || '',
      username:
        distributorDetail.adminUsername ||
        distributorDetail.username?.replaceAll(' ', '')?.toLowerCase() ||
        '',
      password: '',
    },
    shouldUnregister: true,
  });

  const navigate = useNavigate();
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [enableLogin, setEnableLogin] = useState(
    distributorDetail.isLoginEnable
  );
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });
  const [showSwitchUserConfirmation, setShowSwitchUserConfirmation] =
    useState(false);

  const handleLoginAsDistributor = () => {
    const autoLoginPayload = {
      id: distributorDetail.id,
      deviceDetails: {
        os: getDeviceOs(),
        model: getDeviceModel(),
      },
    };
    loginAsAdminUser(autoLoginPayload)
      .then((res) => {
        const url = `/auto-login?auth=${res.data?.token || ''}`;
        navigate(url);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
      });
  };

  const handleEnableLogin = (payload) => {
    createNewCredential(payload)
      .then(() => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'success',
          message: 'Login Enabled successfully.',
        });
        formValueChangedCallback(false);
        handleRefreshData();
        setFormSubmitted(false);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setFormSubmitted(false);
      });
  };

  const updateLoginData = (payload) => {
    updateDataById(distributorDetail.adminUserId, payload)
      .then(() => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'success',
          message: 'Distributor Login details updated successfully.',
        });
        formValueChangedCallback(false);
        handleRefreshData();
        setFormSubmitted(false);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setFormSubmitted(false);
      });
  };

  const handleDisableLogin = (id) => {
    deleteDataById(id)
      .then(() => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'success',
          message: 'Login disabled successfully.',
        });
        formValueChangedCallback(false);
        handleRefreshData();
        setFormSubmitted(false);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setFormSubmitted(false);
      });
  };

  const onFormSubmit = (data) => {
    setFormSubmitted(true);
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    if (enableLogin) {
      const accessRights = {};
      Object.values(MODULE_ACCESS).forEach((module) => {
        module.rows.forEach((r) => {
          if (r.value === 'video_library' || r.value === 'manual_assignment') {
            accessRights[r.value] = ['view'];
          } else if (r.value === 'license_management') {
            accessRights[r.value] = distributorDetail.canEditLicense
              ? ['view', 'manage']
              : ['view'];
          } else {
            accessRights[r.value] = [];
          }
        });
      });

      const payload = {
        name: data.name,
        email: data.email,
        username: data.username,
        password: data.password,
        role: 'CONTENT_OWNER',
        status: 1,
        accessRights,
      };

      if (!distributorDetail.isLoginEnable) {
        payload.distributorId = distributorDetail.id;

        handleEnableLogin(payload);
      } else {
        payload.channels = [];
        payload.autoApproveContent = distributorDetail.autoApproveContent || 0;

        updateLoginData(payload);
      }
    } else {
      handleDisableLogin(distributorDetail.adminUserId);
    }
  };

  useEffect(() => {
    if (!enableLogin) {
      unregister(['name', 'username', 'email', 'password']);
    }
  }, [enableLogin]);

  useEffect(() => {
    if (Object.keys(dirtyFields).length > 0) {
      formValueChangedCallback(true);
    } else {
      formValueChangedCallback(false);
    }
  }, [Object.keys(dirtyFields).length]);

  return (
    <Box component={Paper} padding={2}>
      <form id="content-owner-login-form" onSubmit={handleSubmit(onFormSubmit)}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={enableLogin}
                  onChange={(e) => setEnableLogin(e.target.checked)}
                  inputProps={{ 'aria-label': 'controlled' }}
                  color="primary"
                  disabled={formSubmitted}
                />
              }
              label="Enable Login"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} />

          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="name"
              control={control}
              rules={enableLogin ? validationRules.REQUIRED : {}}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Full Name *"
                  type="text"
                  error={enableLogin && !!errors.name}
                  helperText={
                    enableLogin ? errors?.name?.message || null : null
                  }
                  fullWidth
                  variant="filled"
                  disabled={!enableLogin}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="username"
              control={control}
              rules={enableLogin ? validationRules.REQUIRED : {}}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Username *"
                  type="text"
                  error={enableLogin && !!errors.username}
                  helperText={
                    enableLogin ? errors?.username?.message || null : null
                  }
                  fullWidth
                  variant="filled"
                  disabled={!enableLogin}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="email"
              control={control}
              rules={enableLogin ? validationRules.REQUIRED_EMAIL : {}}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Portal Email (Different from account email address) *"
                  type="text"
                  error={enableLogin && !!errors.email}
                  helperText={
                    enableLogin ? errors?.email?.message || null : null
                  }
                  fullWidth
                  variant="filled"
                  disabled={!enableLogin}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="password"
              control={control}
              rules={
                !distributorDetail.isLoginEnable && enableLogin
                  ? {
                      ...validationRules.REQUIRED,
                      ...validationRules.PASSWORD,
                    }
                  : {}
              }
              render={({ field }) => (
                <TextField
                  {...field}
                  label={
                    distributorDetail.isLoginEnable ? 'Password' : 'Password *'
                  }
                  type={showPassword ? 'text' : 'password'}
                  error={
                    !distributorDetail.isLoginEnable &&
                    enableLogin &&
                    !!errors.password
                  }
                  helperText={
                    // eslint-disable-next-line no-nested-ternary
                    !distributorDetail.isLoginEnable && enableLogin
                      ? errors?.password?.message || null
                      : distributorDetail.isLoginEnable
                      ? 'Enter password only if you want to change the password of the user login. Otherwise leave it empty so password will not be updated.'
                      : null
                  }
                  fullWidth
                  variant="filled"
                  disabled={!enableLogin}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          title={
                            !showPassword ? 'Show password' : 'Hide password'
                          }
                        >
                          <Iconify
                            icon={
                              !showPassword
                                ? 'eva:eye-fill'
                                : 'eva:eye-off-fill'
                            }
                          />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Grid>
        </Grid>
      </form>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ py: 2.5 }}
      >
        <Button
          type="submit"
          form="content-owner-login-form"
          color="primary"
          variant="contained"
          disabled={
            formSubmitted || (!distributorDetail.isLoginEnable && !enableLogin)
          }
        >
          Save Login
        </Button>
        <Typography variant="body2" color="secondary">
          * indicates a required field
        </Typography>
      </Stack>

      {!!distributorDetail.isLoginEnable && (
        <Stack direction="row" alignItems="center" pt={2} gap={1}>
          <Typography variant="subtitle1" color="secondary">
            Login as {distributorDetail.adminEmail || distributorDetail.email}{' '}
            user?
          </Typography>
          <Button
            type="button"
            size="small"
            color="primary"
            variant="contained"
            disabled={formSubmitted}
            onClick={() => setShowSwitchUserConfirmation(true)}
          >
            Click Here
          </Button>
          <Typography variant="subtitle1" color="secondary">
            to switch the user.
          </Typography>
        </Stack>
      )}

      {showSwitchUserConfirmation && (
        <ConfirmPopup
          title={`Login as user - ${
            distributorDetail.adminEmail || distributorDetail.email
          }`}
          message={`Do you want to login as ${
            distributorDetail.adminEmail || distributorDetail.email
          } user? You can not undo this action!`}
          onCancel={() => setShowSwitchUserConfirmation(false)}
          onSuccess={handleLoginAsDistributor}
          successButtonText="Confirm"
          cancelButtonText="Cancel"
          showConfirmationInput
          confirmationInputText="confirm"
        />
      )}

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </Box>
  );
};

ContentOwnerLogin.propTypes = {
  distributorDetail: PropTypes.object.isRequired,
  handleRefreshData: PropTypes.func.isRequired,
  formValueChangedCallback: PropTypes.func.isRequired,
};

export default ContentOwnerLogin;
